import { Box } from '@mui/material'
import React, { FC, useEffect } from 'react'
import { Outlet } from 'react-router-dom'
import ErrorBoundary from '../ErrorBoundary'
import { useDashboardLoaderStore } from '../store/DashboardLoaderStore'
import { CircularProgressPanel } from './common/CircularProgressPanel'
import { ResponsiveAppBar } from './ResponsiveAppBar'
import { useLocation } from 'react-router-dom'
import { CustomersClient } from '../interactors/clients/CustomerClient'
import { useAccountsStore } from '../store/AccountsStore'
import { AccountDtoPermission, CustomerDto } from '../interactors/gen/backendClient'
import { UserNumberAlertBanner } from './UserNumberAlertBanner'
import { FreeTrialBanner } from './FreeTrialBanner'
import { useMemberAccessPolicy } from '../utils/hooks/useMemberAccessPolicy'

const FREE_TRIAL_BANNER_STORAGE_KEY = 'petso_freeTrialBannerFirstSeenAt'
const FREE_TRIAL_BANNER_DURATION_MS = 7 * 24 * 60 * 60 * 1000

const getFreeTrialBannerStorageKey = (customerId: string, accountId?: string | null) => {
  if (accountId) {
    return `${FREE_TRIAL_BANNER_STORAGE_KEY}:${accountId}`
  }
  return `${FREE_TRIAL_BANNER_STORAGE_KEY}:${customerId}`
}

export const DashboardStructure: FC = () => {
  const { isLoading } = useDashboardLoaderStore()

  const accountsStore = useAccountsStore()
  const location = useLocation()
  const connectedAccountId = accountsStore.connectedAccount?.id

  const [customer, setCustomer] = React.useState<CustomerDto | null>(null)
  const [shouldShowFreeTrialBanner, setShouldShowFreeTrialBanner] = React.useState(false)
  const { policy: memberAccessPolicy } = useMemberAccessPolicy(
    accountsStore.connectedAccount?.customerId,
    Boolean(accountsStore.connectedAccount)
  )

  useEffect(() => {
    const fetchCustomer = async () => {
      const customer = await CustomersClient.getCurrentCustomer()
      accountsStore.changeConnectedCustomer(customer)
      setCustomer(customer)
    }

    fetchCustomer()
  }, [accountsStore.changeConnectedAccount])

  const isSubscribed = customer?.subscription?.status === 'active'
  const isPremium = customer?.subscription?.plan === 'premium' && isSubscribed
  const isFreeTrialBannerLocationAllowed = location.pathname !== '/suivi-adoptant/ajouter-adoptant'

  const fallbackMaxMembersNumber = isSubscribed ? 10 : 2
  // Respect backend policy (null means unlimited). Only fallback when policy not yet loaded.
  const maxMembersNumber = memberAccessPolicy ? memberAccessPolicy.maxMembersNumber : fallbackMaxMembersNumber
  const activeMembersCount = accountsStore.members.filter(
    (member) => member.permission !== AccountDtoPermission.Silent
  ).length
  const isMembersFull = memberAccessPolicy
    ? memberAccessPolicy.isOverLimit
    : activeMembersCount > fallbackMaxMembersNumber
  const almostFull =
    maxMembersNumber != null && !isMembersFull && activeMembersCount >= Math.max(maxMembersNumber - 3, 1)

  useEffect(() => {
    if (!isFreeTrialBannerLocationAllowed || !customer?.id) {
      setShouldShowFreeTrialBanner(false)
      return
    }

    const storageKey = getFreeTrialBannerStorageKey(customer.id, connectedAccountId)
    const now = Date.now()

    try {
      const storedValue = localStorage.getItem(storageKey)
      if (!storedValue) {
        localStorage.setItem(storageKey, String(now))
        setShouldShowFreeTrialBanner(true)
        return
      }

      const firstSeenAt = Number(storedValue)
      if (!Number.isFinite(firstSeenAt)) {
        localStorage.setItem(storageKey, String(now))
        setShouldShowFreeTrialBanner(true)
        return
      }

      setShouldShowFreeTrialBanner(now - firstSeenAt < FREE_TRIAL_BANNER_DURATION_MS)
    } catch (error) {
      setShouldShowFreeTrialBanner(true)
    }
  }, [customer?.id, connectedAccountId, isFreeTrialBannerLocationAllowed])

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100vh',
          maxHeight: '100vh',
          overflowY: 'hidden',
        }}
      >
        <Box sx={{ position: 'relative', maxHeight: '100%', height: '100%', overflowY: 'hidden' }}>
          <ResponsiveAppBar customerTypes={customer?.types} />

          <Box className="scrollable-content" sx={{ maxHeight: '100%', height: '100%', overflowY: 'auto' }}>
            {isFreeTrialBannerLocationAllowed && shouldShowFreeTrialBanner && <FreeTrialBanner />}
            {!isPremium && maxMembersNumber != null && (
              <UserNumberAlertBanner isMembersFull={isMembersFull} almostFull={almostFull} />
            )}
            {isLoading && <CircularProgressPanel />}
            <ErrorBoundary>
              <Outlet />
            </ErrorBoundary>
          </Box>
        </Box>
      </Box>
    </>
  )
}
